import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiHandler } from "../../../helper/ApiHandler";
import { clearUser } from "../../../redux/slices/userSlice";
import Breadcrumb from "../../../components/Breadcrumb";
import SEO from "../../../utils/Seo";

function Profile() {
  const { userLoginToken } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    domain: "",
    app_name: "",
    full_name: "",
    email: "",
    cell: "",
    company_name: "",
    company_address: "",
    gst_no: "",
  });

  const [passwordDetails, setPasswordDetails] = useState({
    oldPassword: "",
    password: "",
    confirmPassword: "",
  });

  const [errorMessage, setErrorMessage] = useState("");

  const {
    domain,
    app_name,
    full_name,
    email,
    cell,
    company_name,
    company_address,
    gst_no,
  } = formData;

  const { oldPassword, password, confirmPassword } = passwordDetails;

  const fetchProfileData = async () => {
    try {
      const response = await ApiHandler(
        `/get_profile.php?_ts=${Date.now()}`,
        "GET",
        undefined,
        userLoginToken,
        dispatch,
        navigate
      );
      if (response && response.data && response.data.status === "1") {
        const data = response.data.data[0];
        setFormData({
          domain: data.domain || "",
          app_name: data.app_name || "",
          full_name: data.full_name || "",
          email: data.email || "",
          cell: data.cell || "",
          company_name: data.company_name || "",
          company_address: data.company_address || "",
          gst_no: data.gst_no || "",
        });
      } else {
        toast.error("Failed to fetch profile data.");
      }
    } catch (error) {
      toast.error("Failed to fetch profile data.");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const handleInputChange = (e, isPassword = false) => {
    const { name, value } = e.target;
    if (isPassword) {
      setPasswordDetails({ ...passwordDetails, [name]: value });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const isFormValid = () => {
    return domain !== "" && full_name !== "" && email !== "" && cell !== "";
  };

  const handleEditProfile = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      toast.warning("Please fill in all required fields correctly.");
      return;
    }

    const body = {
      domain,
      app_name,
      full_name,
      email,
      cell,
      company_name,
      company_address,
      gst_no,
    };

    try {
      const response = await ApiHandler(
        "/edit_profile.php",
        "POST",
        body,
        userLoginToken,
        dispatch,
        navigate
      );
      if (response && response.data && response.data.status === "1") {
        toast.success("Profile updated successfully.");
        fetchProfileData();
      } else {
        toast.error(response.data.msg || "Failed to update profile.");
      }
    } catch (error) {
      toast.error("An error occurred while updating the profile.");
      console.error(error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!oldPassword || !password || !confirmPassword) {
      setErrorMessage("Please fill in all password fields.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("New password and confirm password do not match.");
      return;
    }

    const payload = {
      old_password: oldPassword,
      password: password,
      confirm_password: confirmPassword,
    };

    try {
      const response = await ApiHandler(
        "/change_password.php",
        "POST",
        payload,
        userLoginToken,
        dispatch,
        navigate
      );
      if (response && response.data && response.data.status === "1") {
        toast.success(response.data.msg);
        dispatch(clearUser());
        navigate("/login");
      } else {
        toast.error(response.data.msg || "Failed to change password.");
      }
    } catch (error) {
      toast.error("An error occurred while changing the password.");
      console.error(error);
    }
  };

  const fieldWrap = "flex flex-col min-w-0 w-full";
  const labelClass =
    "text-[11px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 sm:mb-1.5 truncate";
  const baseInput =
    "w-full min-w-0 max-w-full box-border border border-gray-200 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-colors";
  const inputLg = `${baseInput} px-3 sm:px-4 py-2.5 text-sm sm:text-base`;
  const inputMd = `${baseInput} px-2.5 sm:px-3.5 py-2.5 text-sm`;
  const inputSm = `${baseInput} px-2.5 sm:px-3 py-2.5 text-sm`;

  return (
    <>
      <SEO
        title="My Profile - MyAd360"
        description="Update your personal and business information securely on MyAd360."
      />

<Breadcrumb pageName="Profile" />

      <div className="w-full max-w-full overflow-x-hidden box-border px-2 xs:px-3 sm:px-0 mt-4 sm:mt-6">
      <div className="w-full max-w-5xl mx-auto space-y-4 sm:space-y-6 box-border">
        {/* Profile Form */}
        <div className="w-full max-w-full box-border bg-gradient-to-br from-orange-50 via-white to-white rounded-2xl shadow-sm border border-orange-100 p-3 xs:p-4 sm:p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-1.5 h-6 bg-orange-500 rounded-full" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Profile</h2>
          </div>

          <form onSubmit={handleEditProfile} className="space-y-8">
            {/* User Information */}
            <div>
              <p className="text-sm font-semibold text-orange-600 mb-4">
                User Information
              </p>

              {/* Full Name — full width */}
              <div className="mb-4 w-full min-w-0">
                <div className={fieldWrap}>
                  <label className={labelClass}>Full Name</label>
                  <input
                    type="text"
                    name="full_name"
                    placeholder="e.g. Ashvin Santoki"
                    className={inputLg}
                    value={full_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* App Name + App Url — side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 w-full min-w-0">
                <div className={fieldWrap}>
                  <label className={labelClass}>App Name</label>
                  <input
                    type="text"
                    name="app_name"
                    placeholder="e.g. MyAd360"
                    className={inputMd}
                    value={app_name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={fieldWrap}>
                  <label className={labelClass}>App Url</label>
                  <input
                    type="text"
                    name="domain"
                    placeholder="https://www.yourapp.com/"
                    className={inputMd}
                    value={domain}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Email + Cell — side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 w-full min-w-0">
                <div className={fieldWrap}>
                  <label className={labelClass}>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    className={inputMd}
                    value={email}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={fieldWrap}>
                  <label className={labelClass}>Cell</label>
                  <input
                    type="tel"
                    name="cell"
                    placeholder="+91 98765 43210"
                    className={inputMd}
                    value={cell}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="pt-6 border-t border-gray-100">
              <p className="text-sm font-semibold text-orange-600 mb-4">
                Company Information
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 w-full min-w-0">
                <div className={`${fieldWrap} lg:col-span-2`}>
                  <label className={labelClass}>Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    placeholder="e.g. MyAd360 Pvt. Ltd."
                    className={inputMd}
                    value={company_name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={fieldWrap}>
                  <label className={labelClass}>GST No</label>
                  <input
                    type="text"
                    name="gst_no"
                    placeholder="22AAAAA0000A1Z5"
                    className={inputSm}
                    value={gst_no}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className={fieldWrap}>
                <label className={labelClass}>Company Address</label>
                <textarea
                  name="company_address"
                  rows={3}
                  placeholder="Building, street, city, state — PIN code"
                  className={`${inputMd} resize-none`}
                  value={company_address}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="flex justify-stretch sm:justify-start">
              <button
                type="submit"
                className="w-full sm:w-auto bg-orange-500 text-white px-8 py-2.5 rounded-lg font-medium text-sm hover:bg-orange-600 active:bg-orange-700 transition-colors shadow-sm"
              >
                Update Profile
              </button>
            </div>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-gradient-to-br from-orange-50 via-white to-white rounded-2xl shadow-sm border border-orange-100 p-4 sm:p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-1.5 h-6 bg-orange-500 rounded-full" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
              Change Password
            </h2>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 w-full min-w-0">
              <div className={fieldWrap}>
                <label className={labelClass}>Old Password</label>
                <input
                  type="password"
                  name="oldPassword"
                  placeholder="Enter current password"
                  className={inputMd}
                  value={oldPassword}
                  onChange={(e) => handleInputChange(e, true)}
                />
              </div>
              <div className={fieldWrap}>
                <label className={labelClass}>New Password</label>
                <input
                  type="password"
                  name="password"
                  placeholder="Enter new password"
                  className={inputMd}
                  value={password}
                  onChange={(e) => handleInputChange(e, true)}
                />
              </div>
              <div className={fieldWrap}>
                <label className={labelClass}>Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Re-enter new password"
                  className={inputMd}
                  value={confirmPassword}
                  onChange={(e) => handleInputChange(e, true)}
                />
              </div>
            </div>

            {errorMessage && (
              <div className="text-sm text-rose-500 font-medium">
                {errorMessage}
              </div>
            )}

            <div className="flex justify-stretch sm:justify-start">
              <button
                type="submit"
                className="w-full sm:w-auto bg-orange-500 text-white px-8 py-2.5 rounded-lg font-medium text-sm hover:bg-orange-600 active:bg-orange-700 transition-colors shadow-sm"
              >
                Change Password
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </>
  );
}

export default Profile;